import { Arrow } from '@arvesta-websites/icons';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import { getSrc } from 'gatsby-plugin-image';
import { useIntl } from 'react-intl';
import type { CardProps } from 'rebass';
import { tv } from 'tailwind-variants';

import { useAuth } from '../../../shared/auth/authContext';
import Cta from '../../Cta';
import { withErrorBoundary } from '../../ErrorBoundary';
import Lock from '../../Lock/Lock';

import {
  StyledCard,
  StyledContentBox,
  StyledCta,
  StyledCtaWrapper,
  StyledDescription,
  StyledImageBox,
  StyledImageBoxLink,
  StyledTitle,
} from './Styled';

export interface CategoryCardProps extends CardProps {
  /** category id */
  id: string;
  /** card title */
  title: string;
  /** optional card description */
  shortDescription?: string | null;
  /** card image */
  image?:
    | { alt?: string; asset: { gatsbyImageData: IGatsbyImageData }; title: string }
    | IGatsbyImageData
    | string
    | undefined;

  /** card cta */
  link: string;
  /** display link text */
  showLink?: boolean;
  /** Is the content type behind a gate or not? */
  gated?: boolean;
}

const styling = tv({
  slots: {
    imageWrapper: 'relative',
  },
});

const getImage = (image: { asset: { gatsbyImageData: IGatsbyImageData } } | IGatsbyImageData | string | undefined) => {
  if (!image) {
    return '';
  }

  if (typeof image === 'string') {
    return image;
  }

  if ('asset' in image) {
    return getSrc(image.asset.gatsbyImageData);
  }

  return getSrc(image);
};

const CategoryCard = ({ title, shortDescription, image, link, gated, showLink = true, ...rest }: CategoryCardProps) => {
  const intl = useIntl();

  const gatedContentAvailable = process.env.GATED_CONTENT === 'true';
  const isLockedForNonAuthenticatedUsers = gatedContentAvailable && gated;
  const auth = useAuth();
  if (!auth) {
    return null;
  }
  const { state } = auth;
  const { imageWrapper } = styling();

  return (
    <StyledCard {...rest}>
      <StyledImageBoxLink to={link} className={imageWrapper()}>
        <StyledImageBox
          sx={{
            backgroundImage: `url(${getImage(image)})`,
          }}
        />
        {isLockedForNonAuthenticatedUsers && !state.isAuthenticated && <Lock />}
      </StyledImageBoxLink>

      <StyledContentBox>
        <StyledTitle>
          <Cta to={link}>{title}</Cta>
        </StyledTitle>

        <StyledDescription>{shortDescription}</StyledDescription>

        {showLink && (
          <StyledCtaWrapper>
            <StyledCta to={link}>
              <Arrow />
              <span>{intl.formatMessage({ id: 'cards.category.link' })}</span>
            </StyledCta>
          </StyledCtaWrapper>
        )}
      </StyledContentBox>
    </StyledCard>
  );
};

export default withErrorBoundary(CategoryCard, { componentName: 'CategoryCard' });
